import type { DataChoice, FieldViewProps, Localization } from '../../types'
import type { FilterField } from '../useFilterField'
import type MultiSelectController from './Controller'

import React, { useMemo } from 'react'
import { prop, uniqBy } from 'lodash/fp'

import { cn } from '@/shared/lib/utils'

import Combobox from '../../components/Combobox'
import CreatebleMultiSelectField from '../../components/CreatebleMultiSelectField'
import ReferencePicker from '../../components/ReferencePicker'
import ReferenceWrapper from '../../components/ReferenceWrapper'
import SelectField from '../../components/SelectField'
import { Operators } from '../../constants'
import { toOperationValueOption } from '../../utils/utils'
import { useFilterField } from '../useFilterField'

type SelectViewProps = FieldViewProps<MultiSelectController>
export default function MultiSelectView({
	field,
	locale,
	invalidMessage,
	storedValue,
	onChange,
	disabled,
	fields,
	enableReferencing,
}: SelectViewProps) {
	const { useSelectOptions } = field
	const selectOptions = useSelectOptions?.() || { data: [] }
	const filterField = useFilterField<MultiSelectController>(field, {
		initialState: storedValue,
		onChange,
	})
	const operation = filterField.getFilterOperation()
	const mode = filterField.getFilterMode()

	const options: DataChoice[] = useMemo(
		() => uniqBy(prop('value'), selectOptions.data || []) || [],
		[storedValue, selectOptions.data]
	)

	const filterTypes = field.getFilterTypes().map((t) => ({
		...t,
		label: locale.getOperatorLabel(t.operation),
	}))
	const operators = filterTypes.map(toOperationValueOption)!
	const selectedTilterType = filterTypes.find((t) => t.operation === operation)!
	const isInvalid = Boolean(invalidMessage)

	return (
		<>
			{!!operation && filterTypes.length < 2 ? null : (
				<div className='flex items-center'>
					<Combobox
						locale={locale}
						data-testid='query-builder-operation-select'
						name='operation'
						placeholder={locale.getOperationPlaceholder()}
						className='w-full'
						disabled={disabled}
						initialOpen={!operation}
						invalid={isInvalid}
						value={operation}
						onChange={filterField.setFilterOperation}
						options={operators}
					/>
				</div>
			)}
			<div
				className={cn(
					'col-span-2 flex items-center',
					!!operation && filterTypes.length < 2 ? 'col-span-3' : 'col-span-2'
				)}
			>
				{selectedTilterType?.hasInput ? (
					enableReferencing ? (
						<ReferenceWrapper filterField={filterField} locale={locale}>
							{mode === 'reference' ? (
								<ReferencePicker
									locale={locale}
									filterField={filterField}
									fields={fields}
									isInvalid={isInvalid}
								/>
							) : (
								<MultiSelectEditor
									locale={locale}
									filterField={filterField}
									placeholder={
										field.placeholder || locale.getSelectValuePlaceholder()
									}
									invalid={isInvalid}
									disabled={disabled}
									options={options}
									isLoading={selectOptions.isLoading}
								/>
							)}
						</ReferenceWrapper>
					) : (
						<MultiSelectEditor
							locale={locale}
							filterField={filterField}
							placeholder={
								field.placeholder || locale.getSelectValuePlaceholder()
							}
							invalid={isInvalid}
							disabled={disabled}
							options={options}
							isLoading={selectOptions.isLoading}
						/>
					)
				) : null}
			</div>
		</>
	)
}

const MultiSelectEditor = ({
	filterField,
	placeholder,
	invalid,
	disabled,
	locale,
	options,
	isLoading,
	...props
}: {
	placeholder: string
	filterField: FilterField<MultiSelectController>
	invalid?: boolean
	disabled?: boolean
	locale: Localization
	options: DataChoice[]
	isLoading?: boolean
}) => {
	const operation = filterField.getFilterOperation()
	const value = filterField.getFilterValue()
	const commonProps = {
		isLoading,
		options,
		placeholder,
		invalid,
		disabled,
		locale,
	}

	return [
		Operators.IN,
		Operators.NOT_IN,
		Operators.ANY_OF,
		Operators.NONE_OF,
		Operators.ALL_OF,
		Operators.EXACT,
		Operators.DESCENDANT_OF,
	].includes(operation) ? (
		<CreatebleMultiSelectField
			data-testid='query-builder-value-multi-select'
			defaultValue={value as string[]}
			value={value as string[]}
			onValueChange={filterField.setFilterValue}
			{...commonProps}
			{...props}
		/>
	) : (
		<SelectField
			data-testid='query-builder-value-select'
			className='w-full'
			onChange={filterField.setFilterValue}
			value={value as string}
			{...commonProps}
			{...props}
		/>
	)
}
